import { toast } from "sonner";
import { readBelFor, writeBelFor } from "@/lib/belSession";
import { useEffect, useState } from "react";
import { getScripts } from "@/lib/apiClient";
import { ChevronRight, Target, Phone, GitBranch, CornerDownRight, List, Network } from "lucide-react";

export default function LiveScript({ embedded = false, initialScriptId, sessionKey }) {
  const [scripts, setScripts] = useState([]);
  const [activeScript, setActiveScript] = useState(0);
  const [activeStage, setActiveStage] = useState(0);
  const [history, setHistory] = useState([]);
  const [view, setView] = useState("tree");

  useEffect(() => {
    getScripts().then((s) => setScripts(s)).catch(() => {});
  }, []);

  useEffect(() => {
    const idx = scripts.findIndex((s) => s.id === initialScriptId);
    const saved = sessionKey ? readBelFor("script", sessionKey) : null;
    if (saved && scripts[saved.s]) { setActiveScript(saved.s); setActiveStage(saved.st || 0); setHistory(saved.h || []); return; }
    if (idx >= 0) { setActiveScript(idx); setActiveStage(0); setHistory([]); }
  }, [scripts, initialScriptId, sessionKey]);

  useEffect(() => {
    if (sessionKey && scripts.length) writeBelFor("script", sessionKey, { s: activeScript, st: activeStage, h: history });
  }, [sessionKey, scripts.length, activeScript, activeStage, history]);

  if (!scripts.length) {
    return <div className={embedded ? "p-4 text-[#5B6675]" : "px-12 py-10 text-[#5B6675]"}>Belscript laden…</div>;
  }

  const script = scripts[activeScript];
  const stage = script.stages[activeStage];
  const callTypeMap = { owners: "owner", seekers: "seeker", partners: "partner" };
  const callType = callTypeMap[script.id] || "owner";

  const goToStageById = (id) => {
    const idx = script.stages.findIndex((s) => s.id === id);
    if (idx >= 0) {
      setHistory((h) => [...h, activeStage]);
      setActiveStage(idx);
    } else {
      toast.error("Deze reactie heeft geen geldige vervolgstap. Laat de admin de route in Beheer → Belscripts controleren.");
    }
  };
  const goBack = () => {
    setHistory((h) => {
      if (!h.length) return h;
      const prev = h[h.length - 1];
      setActiveStage(prev);
      return h.slice(0, -1);
    });
  };
  const switchScript = (i) => {
    setActiveScript(i);
    setActiveStage(0);
    setHistory([]);
  };

  return (
    <div className={embedded ? "fe-fade-up" : "px-6 lg:px-10 py-8 max-w-7xl mx-auto fe-fade-up"}>
      {!embedded && <header className="mb-6">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#16202B]">Belscript</h1>
        <p className="text-[#5B6675] mt-2">Volg de stappen, lees rustig voor. Klik op de reactie van de klant voor de juiste vervolgstap.</p>
      </header>}

      {/* Script type toggle + view mode */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="inline-flex rounded-xl border border-[#E2E7EC] bg-white p-1">
          {scripts.map((s, i) => (
            <button
              key={s.id}
              data-testid={`script-tab-${s.id}`}
              onClick={() => switchScript(i)}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeScript === i ? "bg-[#001C51] text-white" : "text-[#5B6675] hover:bg-[#EDF1F5]"
              }`}
            >
              {s.title}
            </button>
          ))}
        </div>
        <div className="inline-flex rounded-xl border border-[#E2E7EC] bg-white p-1">
          <button
            data-testid="view-tree"
            onClick={() => setView("tree")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              view === "tree" ? "bg-[#16BFBF] text-white" : "text-[#5B6675] hover:bg-[#EDF1F5]"
            }`}
          >
            <Network className="h-4 w-4" strokeWidth={1.75} /> Beslisboom
          </button>
          <button
            data-testid="view-sheet"
            onClick={() => setView("sheet")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              view === "sheet" ? "bg-[#16BFBF] text-white" : "text-[#5B6675] hover:bg-[#EDF1F5]"
            }`}
          >
            <List className="h-4 w-4" strokeWidth={1.75} /> Spiekbrief
          </button>
        </div>
      </div>

      {view === "sheet" ? (
        <Cheatsheet script={script} />
      ) : (
      <div className={embedded ? "space-y-4" : "grid grid-cols-1 lg:grid-cols-3 gap-6"}>
        {embedded && (
          <select data-testid="stage-select" value={activeStage} onChange={(e) => { setHistory((h) => [...h, activeStage]); setActiveStage(Number(e.target.value)); }} className="fe-input">
            {script.stages.map((st, i) => <option key={st.id} value={i}>{st.label}</option>)}
          </select>
        )}
        {/* Left: stages nav */}
        {!embedded && <div className="lg:col-span-1 space-y-2">
          <p className="text-xs uppercase tracking-wide text-[#5B6675] mb-3 font-semibold">{script.subtitle}</p>
          {script.stages.map((st, i) => (
            <button
              key={st.id}
              data-testid={`stage-${st.id}`}
              onClick={() => { setHistory((h) => [...h, activeStage]); setActiveStage(i); }}
              className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-200 ${
                activeStage === i
                  ? "border-[#001C51] bg-[#001C51] text-white"
                  : "border-[#E2E7EC] bg-white text-[#16202B] hover:border-[#001C51]"
              }`}
            >
              <span className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold ${
                activeStage === i ? "bg-white/20" : "bg-[#EDF1F5] text-[#001C51]"
              }`}>{i + 1}</span>
              <span className="font-medium">{st.label}</span>
              {activeStage === i && <ChevronRight className="h-4 w-4 ml-auto" />}
            </button>
          ))}
        </div>}

        {/* Middle: big readable script */}
        <div className={embedded ? "" : "lg:col-span-2"}>
          <div className={`rounded-2xl border border-[#E2E7EC] bg-white ${embedded ? "p-5 lg:p-6" : "p-8 lg:p-10 min-h-[360px]"}`} data-testid="script-panel">
            <div className="flex items-center gap-2 mb-2 text-[#16BFBF]">
              <Phone className="h-4 w-4" strokeWidth={1.75} />
              <span className="text-sm font-semibold uppercase tracking-wide">{stage.label}</span>
            </div>
            <div className="flex items-start gap-2 mb-6 text-[#5B6675] text-sm">
              <Target className="h-4 w-4 mt-0.5 shrink-0 text-[#001C51]" strokeWidth={1.75} />
              <span>{stage.goal}</span>
            </div>
            <div className="space-y-5">
              {stage.lines.map((line, i) => (
                <p key={i} className={`${embedded ? "text-lg" : "text-2xl"} leading-relaxed text-[#16202B] font-medium`}>
                  {line}
                </p>
              ))}
            </div>

            {stage.branches && stage.branches.length > 0 && (
              <div className="mt-8 pt-6 border-t border-dashed border-[#E2E7EC]" data-testid="branch-panel">
                <div className="flex items-center gap-2 mb-3 text-[#001C51]">
                  <GitBranch className="h-4 w-4" strokeWidth={1.75} />
                  <span className="text-sm font-semibold uppercase tracking-wide">Wat zegt de klant?</span>
                </div>
                <div className="space-y-2.5">
                  {stage.branches.map((br, i) => (
                    <button
                      key={i}
                      data-testid={`branch-${stage.id}-${i}`}
                      onClick={() => goToStageById(br.target)}
                      className="w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl border border-[#E2E7EC] bg-[#F6F9FB] text-[#16202B] font-medium hover:border-[#16BFBF] hover:bg-[#EAFBFB] transition-all duration-200 group"
                    >
                      <CornerDownRight className="h-4 w-4 shrink-0 text-[#16BFBF]" strokeWidth={2} />
                      <span>{br.label}</span>
                      <ChevronRight className="h-4 w-4 ml-auto text-[#5B6675] group-hover:text-[#16BFBF] transition-colors" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-between mt-4">
            <button
              data-testid="prev-stage"
              disabled={activeStage === 0 && !history.length}
              onClick={() => (history.length ? goBack() : setActiveStage((s) => Math.max(0, s - 1)))}
              className="px-5 py-2.5 rounded-xl border border-[#E2E7EC] bg-white text-[#5B6675] font-medium disabled:opacity-40 hover:bg-[#EDF1F5] transition-colors"
            >
              {history.length ? "← Terug" : "Vorige"}
            </button>
            <button
              data-testid="next-stage"
              disabled={activeStage === script.stages.length - 1}
              onClick={() => { setHistory((h) => [...h, activeStage]); setActiveStage((s) => Math.min(script.stages.length - 1, s + 1)); }}
              className="px-5 py-2.5 rounded-xl bg-[#001C51] text-white font-medium disabled:opacity-40 hover:bg-[#0A337A] transition-colors"
            >
              Volgende stap
            </button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

const isNote = (line) => /^\s*\[/.test(line) || line.includes("[COACH]") || line.includes("[STILTE") || line.includes("[LUISTER") || line.includes("[CRM") || line.includes("[Zet in de CRM");

function Cheatsheet({ script }) {
  return (
    <div className="rounded-2xl border border-[#E2E7EC] bg-white p-6 lg:p-8 fe-fade-up" data-testid="cheatsheet">
      <div className="flex items-center gap-2 mb-1 text-[#16BFBF]">
        <List className="h-4 w-4" strokeWidth={1.75} />
        <span className="text-sm font-semibold uppercase tracking-wide">Spiekbrief — alle zinnen op één scherm</span>
      </div>
      <p className="text-[#5B6675] text-sm mb-6">{script.subtitle}</p>
      <div className="space-y-6">
        {script.stages.map((st, i) => (
          <div key={st.id} data-testid={`sheet-stage-${st.id}`} className="pb-5 border-b border-dashed border-[#E2E7EC] last:border-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="h-6 w-6 shrink-0 rounded-full bg-[#EDF1F5] text-[#001C51] flex items-center justify-center text-xs font-semibold">{i + 1}</span>
              <h3 className="font-heading font-semibold text-[#16202B]">{st.label}</h3>
            </div>
            <ul className="space-y-1.5 ml-8">
              {st.lines.map((line, j) => (
                isNote(line) ? (
                  <li key={j} className="text-xs text-[#8A93A0] italic leading-relaxed">{line}</li>
                ) : (
                  <li key={j} className="text-[15px] text-[#16202B] leading-relaxed flex gap-2">
                    <span className="text-[#16BFBF] mt-1">•</span><span>{line}</span>
                  </li>
                )
              ))}
            </ul>
            {st.branches && st.branches.length > 0 && (
              <div className="ml-8 mt-2 flex flex-wrap gap-2">
                {st.branches.map((br, k) => (
                  <span key={k} className="text-xs font-medium text-[#0C6E6E] bg-[#EAFBFB] border border-[#D6F3F3] rounded-full px-2.5 py-1">
                    {br.label}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
